import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  CheckCircle, 
  Users, 
  FileText, 
  FolderOpen, 
  Play, 
  Calendar, 
  UserPlus, 
  Mail, 
  Repeat, 
  BarChart3,
  ChevronDown,
  Clock as ClockIcon
} from 'lucide-react';
import './KPICards.scss';
import { Tooltip } from '@mui/material';

const KPICards = ({
  selectedLocation,
  locumHoursData,
  onShiftChange,
  selectedShift,
  loading,
}) => {

 
  const shiftOptions = ["Morning", "Evening", "Night"];

  // Handle shift change
  const handleShiftChange = (e) => {
    const shift = e.target.value;
    onShiftChange(shift);
  };

  // Default values for shift data
  const shiftData = {
    Morning: {
      hours: locumHoursData?.shiftAnalysis?.morningShift || "0 hrs",
      subtitle: "Locum Hours Completed",
      tooltip: "Total hours logged for shifts starting between 6 AM - 1.59 PM",
    },
    Evening: {
      hours: locumHoursData?.shiftAnalysis?.eveningShift || "0 hrs",
      subtitle: "Locum Hours Completed",
      tooltip: "Total hours logged for shifts starting between 2 PM - 9.59 PM",
    },
    Night: {
      hours: locumHoursData?.shiftAnalysis?.nightShift || "0 hrs",
      subtitle: "Locum Hours Completed",
      tooltip: "Total hours logged for shifts starting between 10 PM - 5.59 AM",
    },
  };

  // Safely get values with fallbacks
  const getSafeValue = (value, fallback = "...") => {
    return value !== undefined && value !== null ? value : fallback;
  };

  const gradientCards = [
    {
      title: "Jobs Completed",
      value: Math.round(getSafeValue(locumHoursData?.completedJobsCount, 0)),
      icon: CheckCircle,
      gradient: "purple-blue",
      tooltip: "Total locum jobs completed by all professionals.",
    },
    {
      title: "Total Locum Hours",
      value: Math.round(getSafeValue(locumHoursData?.totalLocumHours, 0)),
      subtitle: selectedLocation
        ? `Across ${selectedLocation}`
        : "Across all branches",
      icon: Clock,
      gradient: "blue-purple",
      tooltip:
        "Total hours worked by all professionals who completed locum jobs.",
    },
    {
      title: "Jobizo Database",
      value: Math.round(getSafeValue(locumHoursData?.jobizoappusers, 0))?.toLocaleString(),
      icon: Users,
      gradient: "purple-red",
      tooltip: "Total registered professionals on Jobizo.",
    },
  ];

  const whiteCards = [
    {
      title: "Total Jobs Posted",
      value: Math.round(getSafeValue(locumHoursData?.totalJobsPostedCount, 0)),
      icon: FileText,
      color: "blue",
      tooltip: "All locum jobs posted on the platform.",
    },
    {
      title: "Open Jobs",
      value: Math.round(getSafeValue(locumHoursData?.openJobsCount, 0)),
      icon: FolderOpen,
      color: "blue",
      tooltip: "Locum jobs currently open or accepted for application.",
    },
    {
      title: "Ongoing Jobs",
      value: Math.round(getSafeValue(locumHoursData?.ongoingJobsCount, 0)),
      icon: Play,
      color: "blue",
      tooltip: "Locum jobs in progress in branch(s).",
    },
    {
      title: "Upcoming Jobs",
      value:
        locumHoursData?.upcomingJobsThisWeekCount !== undefined &&
        locumHoursData?.upcomingJobsNextWeekCount !== undefined
          ? `${Math.round(
              getSafeValue(locumHoursData.upcomingJobsThisWeekCount, 0)
            )}/${Math.round(
              getSafeValue(locumHoursData.upcomingJobsNextWeekCount, 0)
            )}`
          : "0",
      subtitle: "This Week / Next Week",
      icon: Calendar,
      color: "blue",
      tooltip: "Locum jobs about to start in branch(s).",
    },
    {
      title: "Total Locum Hires",
      value: Math.round(getSafeValue(locumHoursData?.totallocumhires, 0)),
      icon: UserPlus,
      color: "blue",
      tooltip: "Professionals hired in locum jobs.",
    },

    {
      title: "Repeat Hire Rate",
      value:
        locumHoursData?.repeatHireRate !== undefined
          ? `${Math.round(getSafeValue(locumHoursData.repeatHireRate, 0))}%`
          : "0",
      icon: Repeat,
      color: "green",
      hasGrowth: true,
      tooltip: "Percentage of professionals repeating in locum jobs.",
    },
    {
      title: "Job Invites Sent",
      value: Math.round(getSafeValue(locumHoursData?.jobinvitessentcount, 0)),
      icon: Mail,
      color: "blue",
      tooltip: "Locum jobs invitations sent to professionals.",
    },
  ];

  const BlinkingDots = () => <span className="blinking-dots">...</span>;

  return (
    <div className="kpi-cards">
      {/* Key Analytics Header */}
      <div className="kpi-cards__section">
        {/* <h2 className="kpi-cards__section-title">Key Analytics</h2> */}

        {/* Gradient Cards */}
        <div className="kpi-cards__gradient-container">
          <div className="kpi-cards__gradient-grid">
            {gradientCards.map((card, index) => (
              <div
                key={index}
                className={`kpi-cards__gradient-card kpi-cards__gradient-card--${card.gradient}`}
              >
                <div className="kpi-cards__gradient-content">
                  <div className="kpi-cards__gradient-info">
                    <h3 className="kpi-cards__gradient-title">{card.title}</h3>
                    <p className="kpi-cards__gradient-value">
                      {loading ? <BlinkingDots /> : card.value}
                    </p>
                    <p className="kpi-cards__gradient-subtitle">
                      {card.subtitle}
                    </p>
                  </div>
                  <div className="kpi-cards__gradient-icon-wrapper">
                    <Tooltip title={card.tooltip} arrow>
                      <card.icon className="kpi-cards__gradient-icon" />
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* White Cards Grid */}
      <div className="kpi-cards__white-grid">
        {whiteCards.map((card, index) => (
          <div key={index} className="kpi-cards__white-card">
            <div className="kpi-cards__white-header">
              <h3 className="kpi-cards__white-title">{card.title}</h3>
              <div className="kpi-cards__white-icon-wrapper">
                <Tooltip title={card.tooltip} arrow>
                  <card.icon className="kpi-cards__white-icon" />
                </Tooltip>
              </div>
            </div>
            <p
              className={`kpi-cards__white-value ${
                card.color === "blue"
                  ? "kpi-cards__white-value--blue"
                  : card.color === "green"
                  ? "kpi-cards__white-value--green"
                  : ""
              }`}
            >
              {loading ? <BlinkingDots /> : card.value}
            </p>
            {card.subtitle && (
              <p
                className={`kpi-cards__white-subtitle ${
                  card.hasGrowth ? "kpi-cards__white-subtitle--growth" : ""
                }`}
              >
                {card.hasGrowth && (
                  <span className="kpi-cards__growth-icon">↗</span>
                )}
                {card.subtitle}
              </p>
            )}
          </div>
        ))}

        {/* Enhanced Shift Analysis Card */}
        <div className="kpi-cards__white-card kpi-cards__shift-card">
          <div className="kpi-cards__white-header">
            <h3 className="kpi-cards__white-title">Worked Hours Summary</h3>
            <div className="kpi-cards__shift-dropdown">
              <select
                value={selectedShift}
                onChange={handleShiftChange}
                // onChange={(e) => setSelectedShift(e.target.value)}
                className="kpi-cards__shift-select"
              >
                {shiftOptions.map((shift) => (
                  <option key={shift} value={shift}>
                    {shift}
                  </option>
                ))}
              </select>
              {/* <ChevronDown className="kpi-cards__shift-select-icon" /> */}
            </div>
          </div>

          <p className="kpi-cards__white-value kpi-cards__white-value--blue">
            {/* {Math.round(locumHoursData?.shiftAnalysis || "")} hrs */}
            {loading ? (
              <BlinkingDots />
            ) : (
              `${Math.round(locumHoursData?.shiftAnalysis || 0)} hrs`
            )}
          </p>
          <p className="kpi-cards__white-subtitle">
            {shiftData[selectedShift].subtitle}
          </p>

          <div className="kpi-cards__shift-icon">
            <Tooltip title={shiftData[selectedShift].tooltip} arrow>
              <BarChart3 className="kpi-cards__shift-icon-svg" />
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPICards;